// Public KYC (Know Your Customer) Form Page
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "../config/api";
import {
  Building2,
  MapPin,
  CreditCard,
  User,
  Mail,
  Phone,
  Globe,
  FileText,
  CheckCircle,
  Loader2,
  AlertCircle,
  Banknote,
} from "lucide-react";


export default function KYCFormPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [kycInfo, setKycInfo] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    // Company Info
    company_name: "",
    company_legal_name: "",
    registration_number: "",
    vat_number: "",
    industry: "",
    website: "",
    
    // Address
    address_street: "",
    address_city: "",
    address_postal_code: "",
    address_country: "",
    
    // Banking
    bank_name: "",
    bank_iban: "",
    bank_swift: "",
    bank_account_holder: "",
    
    // Payment Contact
    payment_contact_name: "",
    payment_contact_email: "",
    payment_contact_phone: "",
    
    // General Contact
    general_contact_name: "",
    general_contact_email: "",
    general_contact_phone: "",
    
    // Preferences
    payment_terms: "",
    preferred_currency: "EUR",
    
    // Notes
    notes: "",
  });

  useEffect(() => {
    fetchKYCInfo();
  }, [token]);

  const fetchKYCInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/kyc/${token}`);
      setKycInfo(response.data);
      
      if (response.data.status === "already_submitted") {
        setSubmitted(true);
      } else {
        // Pre-fill known data
        setFormData(prev => ({
          ...prev,
          company_name: response.data.company_name || "",
          general_contact_name: response.data.contact_name || "",
          general_contact_email: response.data.contact_email || "",
        }));
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired link");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name) {
      toast.error("Company name is required");
      return;
    }
    
    if (!formData.general_contact_email) {
      toast.error("Contact email is required");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API_URL}/kyc/${token}/submit`, formData);
      
      if (response.data.success) {
        setSubmitted(true);
        toast.success("Registration submitted successfully!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit form");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Link Invalid</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Thank You!</h1>
          <p className="text-slate-600 mb-4">
            Your company registration has been submitted successfully.
          </p>
          <p className="text-sm text-slate-500">
            Reference: <strong>{kycInfo?.company_ref}</strong>
          </p>
          <p className="text-sm text-slate-500 mt-4">
            Our team will review your information and contact you shortly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4">
            <Building2 className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Company Registration</h1>
          <p className="text-slate-600">Europacific Logistics - Know Your Customer Form</p>
          {kycInfo?.company_ref && (
            <p className="text-sm text-blue-600 mt-2">Reference: {kycInfo.company_ref}</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Information */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Company Information
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Company Name *</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => handleChange("company_name", e.target.value)}
                  placeholder="Your Company Ltd."
                  required
                />
              </div>
              <div>
                <Label>Legal Name (if different)</Label>
                <Input
                  value={formData.company_legal_name}
                  onChange={(e) => handleChange("company_legal_name", e.target.value)}
                  placeholder="Official legal name"
                />
              </div>
              <div>
                <Label>Registration Number</Label>
                <Input
                  value={formData.registration_number}
                  onChange={(e) => handleChange("registration_number", e.target.value)}
                  placeholder="Company registration number"
                />
              </div>
              <div>
                <Label>VAT Number</Label>
                <Input
                  value={formData.vat_number}
                  onChange={(e) => handleChange("vat_number", e.target.value)}
                  placeholder="e.g., SI12345678"
                />
              </div>
              <div>
                <Label>Industry</Label>
                <select
                  value={formData.industry}
                  onChange={(e) => handleChange("industry", e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                >
                  <option value="">Select industry...</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="retail">Retail / E-commerce</option>
                  <option value="automotive">Automotive</option>
                  <option value="electronics">Electronics</option>
                  <option value="chemicals">Chemicals</option>
                  <option value="food_beverage">Food & Beverage</option>
                  <option value="textiles">Textiles & Apparel</option>
                  <option value="machinery">Machinery</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => handleChange("website", e.target.value)}
                  placeholder="https://www.example.com"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-600" />
              Company Address
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Street Address</Label>
                <Input
                  value={formData.address_street}
                  onChange={(e) => handleChange("address_street", e.target.value)}
                  placeholder="123 Business Street"
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={formData.address_city}
                  onChange={(e) => handleChange("address_city", e.target.value)}
                  placeholder="City"
                />
              </div>
              <div>
                <Label>Postal Code</Label>
                <Input
                  value={formData.address_postal_code}
                  onChange={(e) => handleChange("address_postal_code", e.target.value)}
                  placeholder="12345"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Country</Label>
                <Input
                  value={formData.address_country}
                  onChange={(e) => handleChange("address_country", e.target.value)}
                  placeholder="Country"
                />
              </div>
            </div>
          </div>

          {/* Banking Details */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Banknote className="h-5 w-5 text-blue-600" />
              Banking Details
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Bank Name</Label>
                <Input
                  value={formData.bank_name}
                  onChange={(e) => handleChange("bank_name", e.target.value)}
                  placeholder="Bank name"
                />
              </div>
              <div>
                <Label>Account Holder Name</Label>
                <Input
                  value={formData.bank_account_holder}
                  onChange={(e) => handleChange("bank_account_holder", e.target.value)}
                  placeholder="Name on account"
                />
              </div>
              <div>
                <Label>IBAN</Label>
                <Input
                  value={formData.bank_iban}
                  onChange={(e) => handleChange("bank_iban", e.target.value)}
                  placeholder="SI56 1234 5678 9012 3456"
                />
              </div>
              <div>
                <Label>SWIFT / BIC</Label>
                <Input
                  value={formData.bank_swift}
                  onChange={(e) => handleChange("bank_swift", e.target.value)}
                  placeholder="BANKSIXX"
                />
              </div>
            </div>
          </div>

          {/* Contacts */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600" />
              Contact Information
            </h2>
            
            <div className="mb-6">
              <h3 className="text-sm font-medium text-slate-600 mb-3">General Contact *</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={formData.general_contact_name}
                    onChange={(e) => handleChange("general_contact_name", e.target.value)}
                    placeholder="Contact name"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.general_contact_email}
                    onChange={(e) => handleChange("general_contact_email", e.target.value)}
                    placeholder="email@company.com"
                    required
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.general_contact_phone}
                    onChange={(e) => handleChange("general_contact_phone", e.target.value)}
                    placeholder="+386 1 234 5678"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-slate-600 mb-3">Payment / Accounts Contact</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={formData.payment_contact_name}
                    onChange={(e) => handleChange("payment_contact_name", e.target.value)}
                    placeholder="Accounts contact name"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.payment_contact_email}
                    onChange={(e) => handleChange("payment_contact_email", e.target.value)}
                    placeholder="accounts@company.com"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.payment_contact_phone}
                    onChange={(e) => handleChange("payment_contact_phone", e.target.value)}
                    placeholder="+386 1 234 5678"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Preferences */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-blue-600" />
              Payment Preferences
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Payment Terms</Label>
                <select
                  value={formData.payment_terms}
                  onChange={(e) => handleChange("payment_terms", e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                >
                  <option value="">Select payment terms...</option>
                  <option value="prepaid">Prepaid</option>
                  <option value="net_15">Net 15 days</option>
                  <option value="net_30">Net 30 days</option>
                  <option value="net_45">Net 45 days</option>
                  <option value="net_60">Net 60 days</option>
                </select>
              </div>
              <div>
                <Label>Preferred Currency</Label>
                <select
                  value={formData.preferred_currency}
                  onChange={(e) => handleChange("preferred_currency", e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                >
                  <option value="EUR">EUR - Euro</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="CHF">CHF - Swiss Franc</option>
                </select>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Additional Notes
            </h2>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Any additional information you'd like to share..."
              rows={4}
            />
          </div>

          {/* Submit */}
          <div className="flex justify-center">
            <Button
              type="submit"
              size="lg"
              className="px-12 bg-blue-600 hover:bg-blue-700"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Submit Registration
                </>
              )}
            </Button>
          </div>

          <p className="text-center text-sm text-slate-500">
            By submitting this form, you agree to share this information with Europacific Logistics.
          </p>
        </form>
      </div>
    </div>
  );
}
